import React, { type PropsWithChildren } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { BodyText, Image, theme, TitleText, useResponsive } from '@sg/garnish';

import { LoyaltyHomeChallengesRewardsContainer } from '../LoyaltyHomeChallengesRewardsContainer';

const LoyaltyHomeHowItWorksContainer = (props: ContainerProps) => {
  const { palette, children } = props;

  return (
    <LoyaltyHomeChallengesRewardsContainer style={[styles.container, backgroundPalettes[palette]]}>
      <TitleText style={[styles.textColor, styles.centerText]} sizeMatch={['48']}>
        <FormattedMessage {...messages.title} />
      </TitleText>

      {children}
    </LoyaltyHomeChallengesRewardsContainer>
  );
};

const LoyaltyHomeHowItWorksContent = (props: PropsWithChildren) => {
  const { match } = useResponsive();

  return (
    <View style={match([styles.contentSM, styles.contentSM, styles.content])}>
      {props.children}
    </View>
  );
};

const LoyaltyHomeHowItWorksCard = (props: CardProps) => {
  const { index, title, description, image } = props;

  return (
    <View style={styles.card}>
      {image ? (
        <Image source={{ uri: image }} style={styles.image} />
      ) : (
        <TitleText sizeMatch={['24']} style={[styles.index, styles.textColor]}>
          {index}
        </TitleText>
      )}

      <TitleText style={[styles.textColor]} sizeMatch={['32']}>
        {title}
      </TitleText>

      <BodyText style={styles.textColor} sizeMatch={['14', '14', '18']}>
        {description}
      </BodyText>
    </View>
  );
};

const LoyaltyHomeHowItWorksDisclaimer = (props: ContainerProps) => {
  return (
    <BodyText sizeMatch={['12']} style={[styles.disclaimer, styles.textColor]}>
      {props.children}
    </BodyText>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'How it works',
    description: 'Loyalty Home | How it works | Title',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  lightBackground: {
    backgroundColor: theme.colors.KALE,
  },
  darkBackground: {
    backgroundColor: theme.colors.BLACK,
    borderColor: theme.colors.WHITE,
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  textColor: {
    color: theme.colors.WHITE,
    borderColor: theme.colors.WHITE,
  },
  container: {
    paddingVertical: theme.spacing['20'],
    gap: theme.spacing['10'],
  },
  contentSM: {
    gap: theme.spacing['10'],
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  content: {
    gap: theme.spacing['20'],
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  card: {
    flex: 1,
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  index: {
    width: 32,
    height: 32,
    lineHeight: 32,
    borderRadius: 32,
    borderWidth: 2,
    textAlign: 'center',
  },
  image: {
    width: 32,
    height: 32,
  },
  centerText: {
    textAlign: 'center',
  },
  disclaimer: {
    color: theme.colors.CHARCOAL,
    alignSelf: 'center',
    textAlign: 'center',
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const backgroundPalettes: Record<HowItWorksPalette, ViewStyle> = {
  dark: styles.darkBackground,
  light: styles.lightBackground,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type HowItWorksPalette = 'dark' | 'light';

type ContainerProps = {
  children: PropsWithChildren['children'];
  palette: HowItWorksPalette;
};

type CardProps = {
  index: number;
  title: string;
  description: string;
  image?: string;
  palette: HowItWorksPalette;
};

// ─── Compound Components ────────────────────────────────────────────────────

export const LoyaltyHomeHowItWorks = {
  Container: LoyaltyHomeHowItWorksContainer,
  Content: LoyaltyHomeHowItWorksContent,
  Card: LoyaltyHomeHowItWorksCard,
  Disclaimer: LoyaltyHomeHowItWorksDisclaimer,
};
